"use client";
import Link from "next/link";
import { ChevronLeft } from "lucide-react";
import ImageComponent from "@core/helpers/client/components/image";
import SwiperComponent from "@/components/ui/swiper-component";
import ProductCard from "@/components/ProductCard";
import Title from "../Title";
import DeviceOnly from "@/components/DeviceOnly";

const BrandInfoCard = ({ fields, isLast = false }) => (
    <div className={`h-full w-full flex flex-col items-center justify-center text-center overflow-hidden ${isLast ? 'bg-jmt-white border border-jmt-neutral-200 rounded-md' : ''}`}>

        <div className={`flex flex-col items-center justify-center ${!isLast && "flex-1"}`}>
            {!isLast && (
                <Title
                    type={fields?.title?.type}
                    className="font-bold md:text-xl text-jmt-neutral-600 mb-8"
                >
                    {fields?.title?.title || "محبوب‌ترین برندها"}
                </Title>
            )}

            <div className={`${isLast ? 'w-[100px] lg:w-[120px]' : 'w-[140px]'} h-auto`}>
                <ImageComponent
                    src={fields?.logo?.url || ""}
                    alt={fields?.logo?.alt || ""}
                    width={100}
                    height={100}
                    // باکس لوگو ۱۰۰ / ۱۲۰ / ۱۴۰px
                    sizes="140px"
                    style={{ width: "100%", height: "100%" }}
                    image_className="w-full h-full object-contain"
                />
            </div>

            {!isLast && (
                <p className="text-jmt-neutral-600 font-bold text-sm mt-4">
                    {fields?.description || "بهترین کیفیت "}
                </p>
            )}
        </div>

        <Link
            href={fields?.link?.url || "#"}
            className={`flex items-center justify-center text-sm font-bold text-jmt-neutral-800 hover:text-black transition gap-1  ${!isLast ? "mt-auto" : "mt-4"} `}>
            <span>{fields?.["text-link"] || "مشاهده همه"}</span>
            <ChevronLeft className="w-4 h-4 lg:w-5 lg:h-5" />
        </Link>
    </div>
);

export default function HomePopularProductsBrand({ fields, collection_data }) {
    // کارت‌ها همراه ویجت از کوئری صفحه (getPrefixUrlDetails) می‌آیند؛ fetch کلاینتی نداریم
    const products = collection_data?.products || [];

    const productSlides = products.map((product, index) => (
        <div
            key={`product-${product.id}`}
            className="h-full bg-jmt-white border border-jmt-neutral-200 hover-shadow-full-light rounded-md overflow-hidden"
        >
            <ProductCard product={product} index={index} />
        </div>
    ));

    const desktopSlides = [
        <div key="brand-info-start" className="h-full w-full flex items-center">
            <BrandInfoCard fields={fields} isLast={false} />
        </div>,
        ...productSlides,
        <BrandInfoCard key="brand-info-end" fields={fields} isLast={true} />
    ];

    const mobileSlides = [
        ...productSlides,
        <BrandInfoCard key="brand-info-end-mobile" fields={fields} isLast={true} />
    ];

    return (
        <>
            <DeviceOnly on="mobile">
                <div className="block lg:hidden w-full mx-auto">
                    <div className="w-full bg-jmt-neutral-100 border border-jmt-neutral-200 rounded-md py-4 overflow-hidden">

                        {/* Header */}

                        <div className="flex items-center justify-center gap-2 mb-3 px-4 md:px-10">
                            <Title
                                type={fields?.title?.type}
                                className="shrink-0 whitespace-nowrap font-bold text-jmt-neutral-600"
                            >
                                {fields?.title?.title || "محبوب‌ترین برندها"}
                            </Title>

                            <div className="h-8 shrink-0 flex items-center">
                                <ImageComponent
                                    src={fields?.logo?.url || ""}
                                    alt={fields?.logo?.alt || ""}
                                    width={120}
                                    height={32}
                                    // لوگوی کوچک: ارتفاع ۳۲px، حداکثر عرض ۱۲۰px
                                    sizes="120px"
                                    style={{ width: "auto", height: "100%" }}
                                    image_className="h-8 w-auto max-w-[120px] object-contain"
                                />
                            </div>

                            <p className="min-w-0 truncate text-jmt-neutral-600 font-bold text-[10px]">
                                {fields?.description || "بهترین کیفیت چرم ایران"}
                            </p>
                        </div>

                        {/* Content */}
                        <SwiperComponent
                            slides_shadow_space
                            slides={products?.length > 0 ? mobileSlides : []}
                            navigation={false}
                            grabCursor
                            dir="rtl"
                            swiper_className="px-4! md:px-10!"
                            breakpoints={{
                                380: { slidesPerView: 2.1, spaceBetween: 8 },
                                500: { slidesPerView: 2.8, spaceBetween: 8 },
                                640: { slidesPerView: 3, spaceBetween: 8 },
                                767: { slidesPerView: 3.5, spaceBetween: 12 },
                                920: { slidesPerView: 4, spaceBetween: 12 },
                            }}
                            slidesPerView={1.5}
                            spaceBetween={8}
                        />
                    </div>
                </div>
            </DeviceOnly>

            <DeviceOnly on="desktop">
                <div className="hidden lg:block">
                    <div className="container">
                        <div className="w-full flex items-stretch bg-jmt-neutral-100 border border-jmt-neutral-200 rounded-md p-4 gap-6 overflow-hidden">

                            <div className="w-full overflow-hidden">
                                <SwiperComponent
                                    slides_shadow_space
                                    slides={products?.length > 0 ? desktopSlides : []}
                                    breakpoints={{
                                        1024: { slidesPerView: 4.5, spaceBetween: 16 },
                                        1280: { slidesPerView: 5.5, spaceBetween: 16 },
                                    }}
                                    slidesPerView={1.5}
                                    spaceBetween={8}
                                    navigation={true}
                                    navPosition="sides"
                                    grabCursor={true}
                                    dir="rtl"
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </DeviceOnly>

        </>
    );
}
